/**
 * Fixed Add to Cart Component
 * Handles the fixed bottom add-to-cart button with size selector synchronization
 */
class FixedAddToCart {
  constructor() {
    // Core elements
    this.fixedCart = document.querySelector('[data-fixed-cart]');
    this.fixedForm = this.fixedCart ? this.fixedCart.querySelector('[data-fixed-cart-form]') : null;
    this.mainProductForm = document.querySelector('[data-product-form]');
    
    // Option selectors
    this.fixedOptionSelectors = this.fixedCart ? this.fixedCart.querySelectorAll('popout-select') : [];
    this.fixedVariantInput = this.fixedForm ? this.fixedForm.querySelector('[data-variant-id]') : null;
    
    // Main product selectors
    this.mainOptionSelectors = this.mainProductForm ? this.mainProductForm.querySelectorAll('popout-select[data-popout-prevent="true"]') : [];
    this.mainVariantInput = this.mainProductForm ? this.mainProductForm.querySelector('[name="id"]') : null;
    
    if (this.fixedCart && this.mainProductForm) {
      this.init();
    }
  }

  init() {
    this.setupElements();
    // Get product data and initialize sellout variants
    this.getProductData().then(() => {
        this.initializeSelloutVariants();
        this.updateButtonState();
      });
    this.bindEvents();
    this.configureScrollVisibility();
    this.syncWithMainProduct();
  }

  setupElements() {
    // Ensure we have the correct selectors
    if (this.fixedOptionSelectors.length === 0 && this.fixedCart) {
      this.fixedOptionSelectors = this.fixedCart.querySelectorAll('popout-select');
    }
    if (this.mainOptionSelectors.length === 0 && this.mainProductForm) {
      this.mainOptionSelectors = this.mainProductForm.querySelectorAll('popout-select[data-popout-prevent="true"]');
    }
    
    // Get button element
    if (!this.fixedButton && this.fixedCart) {
      this.fixedButton = this.fixedCart.querySelector('[data-add-to-cart]');
    }
  }

  async getProductData() {
    if (this.productData) {
      return this.productData;
    }

    try {
      // Always try to get from current page's product JSON first
      const productJson = document.querySelector('[data-product-json]');
      if (productJson) {
        try {
          this.productData = JSON.parse(productJson.textContent);
          return;
        } catch (e) {
          console.error('Error parsing product JSON:', e);
        }
      }

      // Try to get current product handle from URL or page
      let productHandle = null;
      
      // Check if we're on a product page
      const currentPath = window.location.pathname;
      const productPathMatch = currentPath.match(/\/products\/([^\/\?]+)/);
      if (productPathMatch) {
        productHandle = productPathMatch[1];
      }
      
      // Fallback to fixed cart's data attribute
      if (!productHandle) {
        productHandle = this.fixedCart ? this.fixedCart.getAttribute('data-product-handle') : null;
      }

      if (productHandle) {
        const response = await fetch(`/products/${productHandle}.js`);
        if (response.ok) {
          this.productData = await response.json();
          return this.productData;
        }
      }

      // Fallback: get product ID from fixed cart data attribute
      const productId = this.fixedCart ? this.fixedCart.getAttribute('data-product-id') : null;
      if (productId) {
        this.productData = {
          id: parseInt(productId),
          variants: [] // Will be populated by theme's existing variant handling
        };
        return;
      }
      
      this.productData = null;
    } catch (error) {
      console.error('Error fetching product data:', error);
      this.productData = null;
    }
  }

  // Reset product data cache to force refresh
  async resetProductData() {
    this.productData = null;
    this.cachedProductHandle = null;
    
    // Immediately fetch new product data
    await this.getProductData();
  }



  initializeSelloutVariants() {
    // Only initialize when we have product data and fixed form
    if (!this.productData || !this.fixedForm) return;
    
    // Ensure fixed form has correct class name and attributes
    this.fixedForm.classList.add('product__form');
    this.fixedForm.setAttribute('data-product-form', '');
    
    // Create custom SelloutVariants implementation
    this.updateSelloutStatus();
  }
  
  // Core method to update sellout status - following original SelloutVariants logic
  updateSelloutStatus() {
    if (!this.productData || !this.fixedForm) return;
    
    // Get current selection state
    const currentSelections = this.getCurrentSelections();
    
    // Get all option wrappers
    const optionWrappers = this.fixedForm.querySelectorAll('[data-selector-wrapper][data-option-position]');
    
    optionWrappers.forEach((wrapper, wrapperIndex) => {
      const positionString = wrapper.getAttribute('data-option-position');
      const position = parseInt(positionString, 10) - 1;
      
      // Get all possible values for this option
      const optionItems = wrapper.querySelectorAll('[data-popout-option]');
      
      optionItems.forEach((optionElement, itemIndex) => {
        const val = optionElement.getAttribute('data-value');
        
        let newVals = [...currentSelections];
        newVals[position] = val;
        
        const found = this.productData.variants.find((element) => {
          // only return true if every option matches our hypothetical selection
          let perfectMatch = true;
          for (let index = 0; index < newVals.length; index++) {
            if (element.options[index] !== newVals[index]) {
              perfectMatch = false;
            }
          }
          return perfectMatch;
        });
        
        // Find corresponding list item
        const listItem = optionElement.closest('.select-popout__item');
        
        if (!listItem) {
          return;
        }
        
        // Follow original SelloutVariants logic
        listItem.classList.remove('sold-out', 'unavailable');
        
        if (typeof found === 'undefined') {
          listItem.classList.add('unavailable');
        } else if (found?.available === false) {
          listItem.classList.add('sold-out');
        }
      });
    });
  }
  
  // Get current selection state - fix fixed cart hierarchy structure
  getCurrentSelections() {
    const selections = [];
    
    // Find all option wrappers
    const optionWrappers = this.fixedForm.querySelectorAll('[data-selector-wrapper][data-option-position]');
    
    optionWrappers.forEach(wrapper => {
      const positionString = wrapper.getAttribute('data-option-position');
      const position = parseInt(positionString, 10) - 1;
      
      // Find hidden input within wrapper
      const hiddenInput = wrapper.querySelector('input[type="hidden"]');
      if (hiddenInput) {
        const value = hiddenInput.value;
        selections[position] = value;
      }
    });
    
    return selections;
  }





  updateButtonState() {
    const button = this.fixedCart ? this.fixedCart.querySelector('.fixed-cart-button') : null;
    const buttonText = button ? button.querySelector('[data-add-to-cart-text]') : null;
    
    if (!button) {
      return;
    }

    // Get current variant
    const currentVariant = this.getCurrentVariant();
    
    // Reset button state
    button.classList.remove('btn--sold-out');
    button.removeAttribute('disabled');
    
    if (!currentVariant) {
      // No variant selected or unavailable
      button.classList.add('btn--sold-out');
      button.setAttribute('disabled', 'disabled');
      if (buttonText) {
        buttonText.textContent = window.theme?.strings?.unavailable || 'Unavailable';
      }
    } else if (!currentVariant.available) {
      // Variant is sold out
      button.classList.add('btn--sold-out');
      button.setAttribute('disabled', 'disabled');
      if (buttonText) {
        buttonText.textContent = window.theme?.strings?.sold_out || 'Sold Out';
      }
    } else {
      // Variant is available
      if (buttonText) {
        // Check if we're on mobile (max-width: 767px)
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        buttonText.textContent = isMobile ? 'Add' : 'Add to Cart';
      }
    }
  }

  getCurrentVariant() {
    if (!this.productData || !this.productData.variants) {
      return null;
    }

    const currentSelections = this.getCurrentSelections();
    
    const foundVariant = this.productData.variants.find(variant => {
      if (!variant.options) return false;
      
      for (let i = 0; i < currentSelections.length; i++) {
        if (variant.options[i] !== currentSelections[i]) {
          return false;
        }
      }
      return true;
    });
    
    return foundVariant;
  }



  // Sync bottom selector and main selector values
  syncSelectors() {
    // Listen for all option input changes
    document.addEventListener('change', (e) => {
      const target = e.target;
      
      // Check if it's an option selector change
      if (target.hasAttribute('data-single-option-selector')) {
        const wrapper = target.closest('[data-selector-wrapper]');
        if (!wrapper) return;
        
        const optionPosition = wrapper.getAttribute('data-option-position');
        if (!optionPosition) return;
        
        // Determine if it's fixed cart or main product selector
        const isFixedCart = wrapper.closest('.fixed-add-to-cart');
        
        if (isFixedCart) {
          // Fixed cart selector changed, update main product selector
          // Find all main product selector wrappers (including visible and hidden)
          const mainWrappers = this.mainProductForm ? this.mainProductForm.querySelectorAll('[data-selector-wrapper]') : [];
          const mainWrapper = Array.from(mainWrappers).find(wrapper => 
            wrapper.getAttribute('data-option-position') === optionPosition
          );
          
          if (mainWrapper) {
            const mainInput = mainWrapper.querySelector('[data-single-option-selector]');
            if (mainInput) {
              mainInput.value = target.value;
              mainInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        } else {
          // Main product selector changed, update fixed cart selector
          this.syncFixedSelectorWithMain();
          this.updateSelloutStatus();
        }
        
        // Update button state
        this.updateButtonState();
      }
    });
  }

  bindEvents() {
    // Listen for theme:popout:click events on fixed cart options
    this.fixedOptionSelectors.forEach(selector => {
      const popoutOptions = selector.querySelectorAll('[data-popout-option]');
      popoutOptions.forEach(option => {
        option.addEventListener('theme:popout:click', (e) => {
          const value = e.currentTarget.getAttribute('data-value');
          const optionPosition = selector.closest('[data-selector-wrapper]')?.getAttribute('data-option-position');
          if (value && optionPosition) {
            this.updateMainProductOption(value, optionPosition);
          }
        });
      });
    });

    // Listen for theme:popout:click events on main product options
    this.mainOptionSelectors.forEach(selector => {
      const mainPopoutOptions = selector.querySelectorAll('[data-popout-option]');
      mainPopoutOptions.forEach(option => {
        option.addEventListener('theme:popout:click', (e) => {
          const value = e.currentTarget.getAttribute('data-value');
          const optionPosition = selector.closest('[data-selector-wrapper]')?.getAttribute('data-option-position');
          if (value && optionPosition) {
            this.updateFixedCartOption(value, optionPosition);
          }
        });
      });
    });

    // Listen for variant changes on main product form
    if (this.mainProductForm) {
      this.mainProductForm.addEventListener('change', (e) => {
        if (e.target.matches('[data-single-option-selector]')) {
          this.syncVariantId();
          this.syncFixedSelectorWithMain();
        }
      });
    }

    // Handle scroll visibility
    window.addEventListener('scroll', this.throttle(() => {
      this.handleScrollVisibility();
    }, 100));

    // Handle resize
    window.addEventListener('resize', this.throttle(() => {
      this.handleScrollVisibility();
    }, 100));

    // Listen for product variant changes
    document.addEventListener('theme:variant:change', async (e) => {
      await this.resetProductData();
      this.syncVariantId();
      this.syncFixedSelectorWithMain();
      this.updateButtonState();
    });
  }

  updateMainProductOption(value, optionPosition) {
    if (!value || !optionPosition) return;

    // Find the main selector for this option position
    const mainSelector = Array.from(this.mainOptionSelectors).find(selector => {
      const wrapper = selector.closest('[data-selector-wrapper]');
      return wrapper && wrapper.getAttribute('data-option-position') === optionPosition;
    });

    if (!mainSelector) return;

    // Update the hidden input
    const hiddenInput = mainSelector.querySelector('[data-popout-input]');
    if (hiddenInput) {
      hiddenInput.value = value;
      
      // Dispatch change events to trigger variant selection
      hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Update the display text
      const displayText = mainSelector.querySelector('[data-popout-text]');
      if (displayText) {
        displayText.textContent = value;
      }
      
      // Update current state classes
      this.updateCurrentState(mainSelector, value);
    }
  }

  updateFixedCartOption(value, optionPosition) {
    if (!value || !optionPosition) return;

    // Find the fixed selector for this option position
    const fixedSelector = Array.from(this.fixedOptionSelectors).find(selector => {
      const wrapper = selector.closest('[data-selector-wrapper]');
      return wrapper && wrapper.getAttribute('data-option-position') === optionPosition;
    });

    if (!fixedSelector) return;

    // Update the hidden input
    const hiddenInput = fixedSelector.querySelector('[data-popout-input]');
    if (hiddenInput) {
      hiddenInput.value = value;
      
      // Update the display text
      const displayText = fixedSelector.querySelector('[data-popout-text]');
      if (displayText) {
        displayText.textContent = value;
      }
      
      // Update current state classes
      this.updateCurrentState(fixedSelector, value);
    }
  }

  updateCurrentState(selector, value) {
    // Remove current state from all options
    const allItems = selector.querySelectorAll('.select-popout__item');
    allItems.forEach(item => {
      item.classList.remove('select-popout__item--current');
      const currentOption = item.querySelector('[data-popout-option]');
      if (currentOption) {
        currentOption.removeAttribute('aria-current');
      }
    });

    // Add current state to selected option
    const selectedOption = selector.querySelector(`[data-popout-option][data-value="${value}"]`);
    if (selectedOption) {
      selectedOption.closest('.select-popout__item').classList.add('select-popout__item--current');
      selectedOption.setAttribute('aria-current', 'true');
    }
  }








  
  syncFixedSelectorWithMain() {
    // Handle synchronization of all option dropdown selectors
    if (this.mainProductForm) {
      // Find all main product selector wrappers (including visible and hidden)
      const mainWrappers = this.mainProductForm.querySelectorAll('[data-selector-wrapper]');
      
      mainWrappers.forEach((wrapper, index) => {
        const optionPosition = wrapper.getAttribute('data-option-position');
        
        if (!optionPosition) return;
        
        // Find corresponding fixed selector (may be visible or hidden)
        const fixedWrapper = this.fixedCart.querySelector(`[data-selector-wrapper][data-option-position="${optionPosition}"]`);
        
        if (!fixedWrapper) return;
        
        // Get main selector value
        const mainInput = wrapper.querySelector('[data-popout-input]');
        const fixedInput = fixedWrapper.querySelector('[data-popout-input]');
        
        if (mainInput && fixedInput) {
          const mainValue = mainInput.value;
          
          // Update fixed selector value
          fixedInput.value = mainValue;
          
          // Only visible selectors need UI element updates
          const fixedSelector = fixedWrapper.querySelector('popout-select');
          if (fixedSelector) {
            // Update display text
            const popoutText = fixedSelector.querySelector('[data-popout-text]');
            if (popoutText) {
              popoutText.textContent = mainValue;
            }
            
            // Update current selected item style
            const currentItem = fixedSelector.querySelector('.select-popout__item--current');
            if (currentItem) {
              currentItem.classList.remove('select-popout__item--current');
            }
            
            const newCurrentItem = fixedSelector.querySelector(`[data-popout-option][data-value="${mainValue}"]`);
            if (newCurrentItem) {
              newCurrentItem.closest('.select-popout__item').classList.add('select-popout__item--current');
              newCurrentItem.setAttribute('aria-current', 'true');
            }
          }
        }
      });
    }
    
    // Update button state after syncing
    this.updateButtonState();
  }

  setupScrollVisibility() {
    // Check if we're on mobile (max-width: 767px)
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    
    if (isMobile) {
      // Mobile: Use scroll distance method based on product__form__wrapper position
      this.setupMobileScrollTrigger();
    } else {
      // Desktop: Use original IntersectionObserver method
      this.setupDesktopScrollVisibility();
    }
  }

  setupMobileScrollTrigger() {
    // Mobile: use product__form__wrapper for more stable detection
    const triggerElement = document.querySelector('.product__form__wrapper');
    
    if (!triggerElement) {
      // Fallback: show after scrolling 300px
      this.setupScrollTrigger(300);
      return;
    }

    // Calculate the trigger point: when element's bottom reaches the top of viewport
    const rect = triggerElement.getBoundingClientRect();
    const calculatedTriggerPoint = rect.bottom + window.scrollY;

    // Set the trigger offset
    this.triggerOffset = calculatedTriggerPoint;

    // Set up scroll listener with throttling
    const handleScroll = this.throttle(() => {
      const currentScrollY = window.scrollY;
      const shouldShowButton = currentScrollY >= this.triggerOffset;
      const buttonCurrentlyVisible = this.fixedCart.classList.contains('is-visible');
      
      if (shouldShowButton && !buttonCurrentlyVisible) {
        this.showFixedButton();
      } else if (!shouldShowButton && buttonCurrentlyVisible) {
        this.hideFixedButton();
      }
    }, 16); // ~60fps

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Handle window resize to recalculate trigger point
    const handleResize = this.throttle(() => {
      const rect = triggerElement.getBoundingClientRect();
      this.triggerOffset = rect.bottom + window.scrollY;
    }, 250);

    window.addEventListener('resize', handleResize, { passive: true });

    // Initial state check
    const initialScrollY = window.scrollY;
    const shouldShowInitially = initialScrollY >= this.triggerOffset;
    
    if (shouldShowInitially) {
      this.showFixedButton();
    } else {
      this.hideFixedButton();
    }
  }

  setupDesktopScrollVisibility() {
    // Desktop: use product description or main product section
    const productDescription = document.querySelector('.product__description, .product-single__description, [data-product-description]');
    const productSection = document.querySelector('.product-single, .product, [data-product-section]');
    const triggerElement = productDescription || productSection;
    
    if (!triggerElement) {
      // Fallback: show after scrolling 300px
      this.setupScrollTrigger(300);
      return;
    }

    // Desktop: original configuration
    const observerConfig = {
      threshold: 0.1,  // Trigger when 10% of element is visible
      rootMargin: '0px'  // No margin adjustment
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Trigger element is visible, hide fixed button
          this.hideFixedButton();
        } else {
          // Trigger element is out of viewport, show fixed button
          this.showFixedButton();
        }
      });
    }, observerConfig);

    observer.observe(triggerElement);
    
    // Listen for window resize to re-setup visibility logic if screen size changes
    window.addEventListener('resize', this.throttle(() => {
      const newIsMobile = window.matchMedia('(max-width: 767px)').matches;
      if (newIsMobile !== isMobile) {
        // Screen size category changed, re-setup visibility
        observer.disconnect();
        this.setupScrollVisibility();
      }
    }, 250));
  }

  setupScrollTrigger(offset) {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (window.scrollY > offset) {
            this.showFixedButton();
          } else {
            this.hideFixedButton();
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
  }

  showFixedButton() {
    if (this.fixedCart && !this.fixedCart.classList.contains('is-visible')) {
      this.fixedCart.classList.add('is-visible');
    }
  }

  hideFixedButton() {
    if (this.fixedCart && this.fixedCart.classList.contains('is-visible')) {
      this.fixedCart.classList.remove('is-visible');
    }
  }

  // Close any open size/option selectors in the fixed add-to-cart area when scrolling or resizing
  // This only targets selectors within the fixed cart to avoid affecting main product selectors
  closeOpenFixedSelectors() {
    if (!this.fixedOptionSelectors || this.fixedOptionSelectors.length === 0) return;

    this.fixedOptionSelectors.forEach(selector => {
      const toggle = selector.querySelector('[data-popout-toggle]');
      const list = selector.querySelector('[data-popout-list]');
      if (!toggle || !list) return;

      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      const isVisible = list.classList.contains('select-popout__list--visible');

      if (isExpanded || isVisible) {
        // Mirror popout-select _hideList behavior: hide list and reset aria-expanded
        list.classList.remove('select-popout__list--visible');
        toggle.setAttribute('aria-expanded', 'false');

        // If the selector is part of a cart item, ensure the open state class is removed
        const cartItem = list.closest('[data-cart-item]');
        if (cartItem) {
          cartItem.classList.remove('cart__item--open');
        }
      }
    });
  }

  syncWithMainProduct() {
    // Sync all selectors from main to fixed
    this.syncFixedSelectorWithMain();
    
    // Sync variant ID
    this.syncVariantId();
  }

  syncVariantId() {
    if (!this.mainVariantInput || !this.fixedVariantInput) return;

    const variantId = this.mainVariantInput.value;
    if (variantId) {
      this.fixedVariantInput.value = variantId;
    }
  }

  configureScrollVisibility() {
    // Use new advanced scroll visibility logic
    this.setupScrollVisibility();
  }

  handleScrollVisibility() {
    if (!this.fixedCart) return;

    // Close any open fixed-cart selectors on scroll/resize
    this.closeOpenFixedSelectors();

    // Show fixed cart after scrolling down 300px
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    if (scrollTop > 300) {
      if (!this.fixedCart.classList.contains('is-visible')) {
        this.fixedCart.classList.add('is-visible');
      }
    } else {
      if (this.fixedCart.classList.contains('is-visible')) {
        this.fixedCart.classList.remove('is-visible');
      }
    }
  }

  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }


}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new FixedAddToCart();
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new FixedAddToCart();
  });
} else {
  new FixedAddToCart();
}